import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const StatusBadge = ({ status }) => {
  const map = {
    operational: { color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', label: 'Operational' },
    degraded: { color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', label: 'Degraded' },
    outage: { color: 'bg-red-100 text-red-700', dot: 'bg-red-500', label: 'Outage' },
    maintenance: { color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', label: 'Maintenance' },
  };
  const s = map?.[status] || map?.operational;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s?.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s?.dot} animate-pulse`} />
      {s?.label}
    </span>
  );
};

const MetricCard = ({ icon, label, value, unit, sub, color, trend, trendUp, loading }) => (
  <div className="bg-card rounded-xl border border-border p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
        <Icon name={icon} size={17} color="currentColor" />
      </div>
    </div>
    {loading ? (
      <div className="h-8 w-24 bg-muted animate-pulse rounded" />
    ) : (
      <div className="text-3xl font-bold text-foreground tracking-tight">
        {value}<span className="text-base font-normal text-muted-foreground ml-1">{unit}</span>
      </div>
    )}
    {(trend || sub) && (
      <div className="flex items-center gap-2 mt-1.5">
        {trend && (
          <span className={`flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
            trendUp ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'
          }`}>
            <Icon name={trendUp ? 'TrendingUp' : 'TrendingDown'} size={10} color="currentColor" />
            {trend}
          </span>
        )}
        {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
      </div>
    )}
  </div>
);

const generateTimeSeriesData = () => {
  const now = new Date();
  return Array.from({ length: 20 }, (_, i) => {
    const t = new Date(now - (19 - i) * 3 * 60000);
    return {
      time: t?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      apiResponse: Math.floor(80 + Math.random() * 120),
      dbQuery: Math.floor(10 + Math.random() * 40),
      errorRate: parseFloat((Math.random() * 1.5)?.toFixed(2)),
      activeUsers: Math.floor(15 + Math.random() * 35),
    };
  });
};

const services = [
  { name: 'Authentication Service', icon: 'ShieldCheck', status: 'operational', uptime: '99.98%', latency: '42ms' },
  { name: 'Database (PostgreSQL)', icon: 'Database', status: 'operational', uptime: '99.99%', latency: '12ms' },
  { name: 'Storage Service', icon: 'HardDrive', status: 'operational', uptime: '99.95%', latency: '68ms' },
  { name: 'Edge Functions', icon: 'Zap', status: 'operational', uptime: '99.90%', latency: '95ms' },
  { name: 'Email Notifications', icon: 'Mail', status: 'operational', uptime: '99.85%', latency: '210ms' },
  { name: 'Payment Gateway', icon: 'CreditCard', status: 'operational', uptime: '99.97%', latency: '180ms' },
  { name: 'Real-time Subscriptions', icon: 'Radio', status: 'operational', uptime: '99.92%', latency: '55ms' },
  { name: 'API Gateway', icon: 'Globe', status: 'operational', uptime: '99.96%', latency: '38ms' },
];

const SystemHealthDashboard = () => {
  const { organizationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [timeSeriesData, setTimeSeriesData] = useState(generateTimeSeriesData);
  const [metrics, setMetrics] = useState({
    activeUsers: 0,
    totalTickets: 0,
    openTickets: 0,
    apiKeysActive: 0,
    avgResponseTime: 94,
    errorRate: 0.3,
    dbConnections: 12,
    uptime: 99.97,
  });
  const [recentErrors, setRecentErrors] = useState([]);

  const fetchMetrics = useCallback(async () => {
    try {
      const queries = [];

      if (organizationId) {
        queries?.push(
          supabase?.from('tickets')?.select('id, status', { count: 'exact', head: false })?.eq('organization_id', organizationId),
          supabase?.from('api_keys')?.select('id', { count: 'exact', head: false })?.eq('organization_id', organizationId)?.eq('status', 'active'),
          supabase?.from('audit_logs')?.select('id, action, created_at, user_id')?.eq('organization_id', organizationId)?.order('created_at', { ascending: false })?.limit(5)
        );
      } else {
        queries?.push(
          supabase?.from('tickets')?.select('id, status', { count: 'exact', head: false }),
          supabase?.from('api_keys')?.select('id', { count: 'exact', head: false })?.eq('status', 'active'),
          supabase?.from('audit_logs')?.select('id, action, created_at, user_id')?.order('created_at', { ascending: false })?.limit(5)
        );
      }

      const [ticketsRes, apiKeysRes, auditRes] = await Promise.all(queries);

      const tickets = ticketsRes?.data || [];
      const openTickets = tickets?.filter(t => t?.status === 'open' || t?.status === 'in_progress')?.length;

      setMetrics(prev => ({
        ...prev,
        totalTickets: tickets?.length,
        openTickets,
        apiKeysActive: apiKeysRes?.count || 0,
        activeUsers: Math.floor(18 + Math.random() * 20),
        avgResponseTime: Math.floor(80 + Math.random() * 40),
        errorRate: parseFloat((Math.random() * 0.8)?.toFixed(2)),
        dbConnections: Math.floor(8 + Math.random() * 10),
      }));

      const errors = (auditRes?.data || [])?.map(log => ({
        id: log?.id,
        message: `${log?.action || 'System event'} recorded`,
        time: new Date(log.created_at)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        level: Math.random() > 0.7 ? 'warning' : 'info',
      }));
      setRecentErrors(errors);
    } catch (err) {
      console.error('Error fetching health metrics:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLastUpdated(new Date());
    }
  }, [organizationId]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      setTimeSeriesData(generateTimeSeriesData());
      fetchMetrics();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchMetrics]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setTimeSeriesData(generateTimeSeriesData());
    await fetchMetrics();
  };

  const overallStatus = services?.every(s => s?.status === 'operational') ? 'operational' : 'degraded';

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>System Health Dashboard - RIGJOCKEY</title></Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground">System Health Dashboard</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Real-time platform metrics and service status
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                Updated {lastUpdated?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
              <button
                onClick={() => setAutoRefresh(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  autoRefresh ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-muted border-border text-muted-foreground'
                }`}
              >
                <Icon name="RefreshCw" size={12} color="currentColor" />
                Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                <Icon name="RefreshCw" size={14} color="currentColor" className={refreshing ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>

          {/* Overall Status Banner */}
          <div className={`rounded-xl border p-4 mb-6 flex items-center gap-4 ${
            overallStatus === 'operational' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
          }`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              overallStatus === 'operational' ? 'bg-emerald-100' : 'bg-amber-100'
            }`}>
              <Icon name={overallStatus === 'operational' ? 'CheckCircle' : 'AlertTriangle'} size={20}
                color={overallStatus === 'operational' ? '#059669' : '#d97706'} />
            </div>
            <div>
              <p className={`font-semibold ${
                overallStatus === 'operational' ? 'text-emerald-800' : 'text-amber-800'
              }`}>
                {overallStatus === 'operational' ? 'All Systems Operational' : 'Some Systems Degraded'}
              </p>
              <p className={`text-sm ${
                overallStatus === 'operational' ? 'text-emerald-600' : 'text-amber-600'
              }`}>
                {services?.length} services monitored · Last checked {lastUpdated?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <div className="ml-auto">
              <StatusBadge status={overallStatus} />
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <MetricCard icon="Users" label="Active Users" value={metrics?.activeUsers} unit="" sub="right now" color="bg-blue-100 text-blue-600" loading={loading} trend={undefined} trendUp={undefined} />
            <MetricCard icon="Zap" label="Avg API Response" value={metrics?.avgResponseTime} unit="ms" trend={metrics?.avgResponseTime < 100 ? 'Fast' : 'Slow'} trendUp={metrics?.avgResponseTime < 100} color="bg-violet-100 text-violet-600" loading={loading} sub={undefined} />
            <MetricCard icon="AlertTriangle" label="Error Rate" value={metrics?.errorRate} unit="%" trend={metrics?.errorRate < 1 ? 'Normal' : 'High'} trendUp={metrics?.errorRate < 1} color={metrics?.errorRate < 1 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'} loading={loading} sub={undefined} />
            <MetricCard icon="Database" label="DB Connections" value={metrics?.dbConnections} unit="" sub="active" color="bg-amber-100 text-amber-600" loading={loading} trend={undefined} trendUp={undefined} />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <MetricCard icon="Ticket" label="Open Tickets" value={metrics?.openTickets} unit="" sub={`of ${metrics?.totalTickets} total`} color="bg-orange-100 text-orange-600" loading={loading} trend={undefined} trendUp={undefined} />
            <MetricCard icon="Key" label="Active API Keys" value={metrics?.apiKeysActive} unit="" color="bg-cyan-100 text-cyan-600" loading={loading} trend={undefined} trendUp={undefined} sub={undefined} />
            <MetricCard icon="Activity" label="Platform Uptime" value={metrics?.uptime} unit="%" trend="30d avg" trendUp color="bg-emerald-100 text-emerald-600" loading={loading} sub={undefined} />
            <MetricCard icon="Server" label="DB Pool Size" value={20} unit="max" sub={`${metrics?.dbConnections} used`} color="bg-slate-100 text-slate-600" loading={loading} trend={undefined} trendUp={undefined} />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* API Response Time */}
            <div className="bg-card rounded-xl border border-border p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-foreground">API Response Times</h3>
                  <p className="text-xs text-muted-foreground">Last 60 minutes (ms)</p>
                </div>
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-medium">Live</span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={timeSeriesData}>
                  <defs>
                    <linearGradient id="apiGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="var(--color-muted-foreground)" />
                  <YAxis tick={{ fontSize: 10 }} stroke="var(--color-muted-foreground)" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Area type="monotone" dataKey="apiResponse" stroke="#6366f1" fill="url(#apiGrad)" strokeWidth={2} name="API (ms)" />
                  <Area type="monotone" dataKey="dbQuery" stroke="#06b6d4" fill="none" strokeWidth={2} strokeDasharray="4 2" name="DB (ms)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Error Rate & Active Users */}
            <div className="bg-card rounded-xl border border-border p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-foreground">Error Rate & Active Users</h3>
                  <p className="text-xs text-muted-foreground">Last 60 minutes</p>
                </div>
                <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full font-medium">Live</span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="var(--color-muted-foreground)" />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} stroke="var(--color-muted-foreground)" />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} stroke="var(--color-muted-foreground)" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line yAxisId="left" type="monotone" dataKey="errorRate" stroke="#ef4444" strokeWidth={2} dot={false} name="Error Rate (%)" />
                  <Line yAxisId="right" type="monotone" dataKey="activeUsers" stroke="#10b981" strokeWidth={2} dot={false} name="Active Users" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Services Status + Recent Events */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Service Status */}
            <div className="lg:col-span-2 bg-card rounded-xl border border-border">
              <div className="p-5 border-b border-border">
                <h3 className="font-semibold text-foreground">Service Status</h3>
                <p className="text-xs text-muted-foreground mt-0.5">All monitored platform services</p>
              </div>
              <div className="divide-y divide-border">
                {services?.map((svc) => (
                  <div key={svc?.name} className="flex items-center justify-between px-5 py-3.5 hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                        <Icon name={svc?.icon} size={15} color="var(--color-muted-foreground)" />
                      </div>
                      <span className="text-sm font-medium text-foreground">{svc?.name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-muted-foreground hidden sm:block">Uptime: <span className="font-semibold text-foreground">{svc?.uptime}</span></span>
                      <span className="text-xs text-muted-foreground hidden sm:block">Latency: <span className="font-semibold text-foreground">{svc?.latency}</span></span>
                      <StatusBadge status={svc?.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Events */}
            <div className="bg-card rounded-xl border border-border">
              <div className="p-5 border-b border-border">
                <h3 className="font-semibold text-foreground">Recent Events</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Latest system activity</p>
              </div>
              <div className="p-4 space-y-3">
                {loading ? (
                  Array.from({ length: 5 })?.map((_, i) => (
                    <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />
                  ))
                ) : recentErrors?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="CheckCircle" size={32} color="#10b981" />
                    <p className="text-sm text-muted-foreground mt-2">No recent events</p>
                  </div>
                ) : (
                  recentErrors?.map((err) => (
                    <div key={err?.id} className={`flex items-start gap-3 p-3 rounded-lg ${
                      err?.level === 'warning' ? 'bg-amber-50 border border-amber-100' : 'bg-blue-50 border border-blue-100'
                    }`}>
                      <Icon
                        name={err?.level === 'warning' ? 'AlertTriangle' : 'Info'}
                        size={14}
                        color={err?.level === 'warning' ? '#d97706' : '#3b82f6'}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{err?.message}</p>
                        <p className="text-xs text-muted-foreground">{err?.time}</p>
                      </div>
                    </div>
                  ))
                )}

                {/* Static fallback events */}
                {!loading && recentErrors?.length === 0 && (
                  <>
                    {[
                      { msg: 'Scheduled backup completed', time: '02:00 AM', level: 'info' },
                      { msg: 'SSL certificate renewed', time: 'Yesterday', level: 'info' },
                      { msg: 'High memory usage detected', time: '2 days ago', level: 'warning' },
                    ]?.map((e, i) => (
                      <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${
                        e?.level === 'warning' ? 'bg-amber-50 border border-amber-100' : 'bg-blue-50 border border-blue-100'
                      }`}>
                        <Icon name={e?.level === 'warning' ? 'AlertTriangle' : 'Info'} size={14}
                          color={e?.level === 'warning' ? '#d97706' : '#3b82f6'} />
                        <div>
                          <p className="text-xs font-medium text-foreground">{e?.msg}</p>
                          <p className="text-xs text-muted-foreground">{e?.time}</p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default SystemHealthDashboard;
